import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import TiptapEditor from "../../../common/components/editor/TiptapEditor";

import api from "../../../../../services/api";
import { notify } from "../../../../../services/toast.config";
import { uploadFileToCloudinary } from "../../../../../utils/uploadFileToCloudinary";
import { all_routes } from "../../../../router/all_routes";
import { getEmployeeOptions } from "../../../reports-settings/report-type/helpers/form.handler";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import { ConfigProvider, Select, Tooltip } from "antd";

dayjs.extend(customParseFormat);

interface CloudinaryAttachment {
  id: string;
  url: string;
  name: string;
  size: number;
  public_id: string;
  resource_type: string;
}

interface CreateReportComponentProps {
  reportType?: any;
  upcomingReportType?: any[];
  allUsers?: any[];
  disableReportTypeChange?: false;
}

interface ReportFormData {
  report_type_id: number;
  report_title: string;
  report_content: string;
  attachments: CloudinaryAttachment[];
  is_draft: boolean;
  status: number;
  shows_to: number[];
  due_date: Dayjs | any;
}

const DEFAULT_FORM_DATA: ReportFormData = {
  due_date: null,
  report_type_id: -1,
  report_title: "Untitled Custom Report",
  report_content: "",
  attachments: [],
  is_draft: false,
  status: 1,
  shows_to: [],
};

const CreateReportComponent: React.FC<CreateReportComponentProps> = ({
  reportType,
  upcomingReportType = [],
  allUsers = [],
  disableReportTypeChange = false,
}) => {
  const navigate = useNavigate();
  const signature = localStorage.getItem("signature");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxFileSize = parseInt(
    process.env.REACT_APP_ERS_MAX_FILE_SIZE_MB || "5"
  );
  const maxAttachments = parseInt(
    process.env.REACT_APP_ERS_MAX_ATTACHMENTS || "5"
  );
  const currentDate = dayjs().format("DD MMM, YYYY");

  const [content, setContent] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCustomReport, setIsCustomReport] = useState<boolean>(true);
  const [formData, setFormData] = useState<ReportFormData>(DEFAULT_FORM_DATA);
  const [selectedReportType, setSelectedReportType] = useState(reportType);
  const [attachments, setAttachments] = useState<CloudinaryAttachment[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [showWarningTooltip, setShowWarningTooltip] = useState<boolean>(false);
  const [selectedReportTypeIndex, setSelectedReportTypeIndex] = useState<number>(-1);

  useEffect(() => {
    if (reportType) {
      if (reportType.id === -1 && upcomingReportType && upcomingReportType.length > 0) {
        // Default to the first upcoming report instead of "Other Report Type"
        const defaultReport = upcomingReportType[0];
        setSelectedReportType(defaultReport);
        setIsCustomReport(false);
        setFormData((prev) => ({
          ...prev,
          report_type_id: defaultReport.id,
          report_title: defaultReport.name,
          due_date: defaultReport.due_date,
        }));
        setSelectedReportTypeIndex(0);

        // Show the warning tooltip briefly
        setShowWarningTooltip(true);
        setTimeout(() => setShowWarningTooltip(false), 5000);
      } else {
        setSelectedReportType(reportType);
        setIsCustomReport(reportType.id === -1);
        setFormData((prev) => ({
          ...prev,
          report_type_id: reportType.id,
          report_title:
            reportType.id === -1 ? "Untitled Custom Report" : reportType.name,
          due_date: reportType?.due_date,
        }));

        if (reportType.id !== -1 && upcomingReportType) {
          const idx = upcomingReportType.findIndex(r => r.id === reportType.id);
          setSelectedReportTypeIndex(idx);
        } else {
          setSelectedReportTypeIndex(-1);
        }
      }
    }
  }, [reportType, upcomingReportType]);

  const handleReportTypeChange = (idx: number) => {
    if (idx === -1) {
      setIsCustomReport(true);
      setSelectedReportType({
        id: -1,
        name: "Custom Report",
        frequency_type: "custom",
      });
      setFormData((prev) => ({
        ...prev,
        report_type_id: -1,
        report_title: "Untitled Custom Report",
      }));
      setSelectedReportTypeIndex(-1);
    } else {
      setIsCustomReport(false);
      const selected = upcomingReportType[idx];
      if (selected) {
        setSelectedReportType(selected);
        setFormData((prev) => ({
          ...prev,
          report_type_id: selected.id,
          report_title: selected.name,
          due_date: selected.due_date,
        }));
        setSelectedReportTypeIndex(idx);
      }
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, report_title: e.target.value }));
  };

  const handleContentChange = (html: string): void => {
    setContent(html);
    setFormData((prev) => ({ ...prev, report_content: html }));
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File exceeds ${maxFileSize}MB limit`;
    }
    return null;
  };

  const handleFileSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newErrors: string[] = [];

    if (attachments.length + fileArray.length > maxAttachments) {
      setErrors([`Maximum ${maxAttachments} files allowed`]);
      return;
    }

    setIsUploading(true);
    const uploadPromises: Promise<CloudinaryAttachment | null>[] = [];

    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
        return;
      }

      const isDuplicate = attachments.some(
        (att) => att.name === file.name && att.size === file.size
      );

      if (isDuplicate) {
        newErrors.push(`${file.name} is already attached`);
        return;
      }

      const uploadPromise = uploadFileToCloudinary({
        file,
        folder: "/ers/reports/attachments",
      })
        .then((response) => ({
          id: Date.now() + Math.random().toString(),
          url: response.secure_url,
          name: file.name,
          size: file.size,
          public_id: response.public_id,
          resource_type: response.resource_type,
        }))
        .catch((error) => {
          console.error(`Upload failed for ${file.name}:`, error);
          newErrors.push(`Failed to upload ${file.name}`);
          return null;
        });

      uploadPromises.push(uploadPromise);
    });

    try {
      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(
        (result): result is CloudinaryAttachment => result !== null
      );

      if (successfulUploads.length > 0) {
        setAttachments((prev) => [...prev, ...successfulUploads]);
        notify.success(
          `${successfulUploads.length} file(s) uploaded successfully`
        );
      }

      if (newErrors.length > 0) {
        setErrors(newErrors);
        notify.error(`${newErrors.length} file(s) failed to upload`);
      } else {
        setErrors([]);
      }
    } catch (error) {
      console.error("Upload process failed:", error);
      notify.error("Upload process failed");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = async (attachmentId: string) => {
    const attachmentToRemove = attachments.find(
      (att) => att.id === attachmentId
    );
    if (!attachmentToRemove) return;

    setAttachments(attachments.filter((att) => att.id !== attachmentId));
    setErrors([]);

    try {
      await api.post("ers/cloudinary/deleteFile", {
        signature: signature,
        public_id: attachmentToRemove.public_id,
        resource_type: attachmentToRemove.resource_type || "auto",
      });
      notify.success("File deleted successfully");
    } catch (error) {
      console.error("Failed to delete file:", error);
      setAttachments((prev) => [...prev, attachmentToRemove]);
      notify.error("Failed to delete file. Please try again.");
    }
  };

  const formatFileSize = (bytes: number): string => {
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + sizes[i];
  };

  const handleSubmit = async (status: "draft" | "submitted") => {
    if (
      !formData.report_title.trim() ||
      formData.report_title === "Untitled Custom Report"
    ) {
      notify.error("Please provide a report title");
      return;
    }

    if (!content.trim()) {
      notify.error("Report content is required");
      return;
    }

    setIsSubmitting(true);

    const reportTitle =
      formData.report_type_id === -1
        ? `${formData.report_title.trim()} (${currentDate})`
        : `${formData.report_title.trim()} (${humanReadableDate(
          formData.due_date
        )})`;

    const reportData = {
      signature: signature,
      report_type_id: formData.report_type_id,
      report_title: reportTitle,
      report_content: content.trim(),
      attachments: attachments,
      is_draft: status === "draft",
      status: status === "draft" ? 0 : 1,
      shows_to: formData.shows_to,
      due_date: formData.due_date || null,
    };

    try {
      const response = await api.post("ers/reports/createReport", reportData);
      notify.success(response?.data?.message);
      navigate(all_routes.ersReportsOverview);
    } catch (e: any) {
      const error = e?.data;
      const errorMessage =
        Array.isArray(error?.message) && error?.message[0]?.msg
          ? error.message[0].msg
          : typeof error?.message === "string" && error.message.trim()
            ? error.message
            : "Internal Server Error!";
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="card mb-4 border-0 shadow-lg position-relative"
        style={{ zIndex: 11 }}>
        <div className="card-body px-4 py-4">
          <div className="mb-1">
            <div className="d-flex flex-wrap justify-content-between align-items-start ">
              <div style={{ width: "500px" }}>
                {isCustomReport ? (
                  <input
                    type="text"
                    value={formData.report_title}
                    onChange={handleTitleChange}
                    className="form-control form-control-sm border-0 bg-transparent p-0   text-primary"
                    style={{
                      outline: "none",
                      boxShadow: "none",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "2",
                    }}
                    placeholder="Enter custom report title..."
                    onFocus={(e) => {
                      if (e.target.value === "Untitled Custom Report") {
                        setFormData((prev) => ({ ...prev, report_title: "" }));
                      }
                    }}
                  />
                ) : (
                  <h4
                    className="text-primary fs-20 fw-normal mb-0"
                    style={{ lineHeight: "2" }}>
                    {`${formData.report_title} (${humanReadableDate(
                      formData?.due_date
                    )})`}
                  </h4>
                )}

                <div className="d-flex align-items-center gap-4 text-muted small">
                  <span>
                    <i className="ti ti-calendar-event me-1"></i>
                    <strong>Date:</strong> {currentDate}
                  </span>
                  <span>
                    <i className="ti ti-clock me-1"></i>
                    <strong>Due:</strong>{" "}
                    {selectedReportType?.due_date
                      ? humanReadableDate(selectedReportType.due_date)
                      : "N/A"}
                  </span>
                  <span>
                    <i className="ti ti-repeat me-1"></i>
                    <strong>Type:</strong>{" "}
                    {selectedReportType?.frequency_type
                      ?.charAt(0)
                      .toUpperCase() +
                      selectedReportType?.frequency_type?.slice(1) || "Custom"}
                  </span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-start ">
                {isCustomReport && !disableReportTypeChange && (
                  <div>
                    <ConfigProvider
                      theme={{
                        components: {
                          Select: {
                            multipleItemHeight: 28,
                            multipleItemBg: "#4c1d95",
                            multipleItemBorderColor: "#7c3aed",
                            colorText: "#ede9fe",
                            colorTextPlaceholder: "#9ca3af",
                            colorBgContainer: "#ffffff",
                            colorTextBase: "#1f2937",
                            zIndexPopup: 1060,
                          },
                        },
                      }}>
                      <Select
                        mode="multiple"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{
                          width: "330px",
                        }}
                        placeholder="Share with other users..."
                        value={formData.shows_to}
                        onChange={(selectedKeys) => {
                          setFormData((prev) => ({
                            ...prev,
                            shows_to: selectedKeys,
                          }));
                        }}
                        options={getEmployeeOptions(allUsers)}
                        maxTagCount="responsive"
                        optionRender={(option) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "0",
                            }}>
                            <img
                              src={
                                option.data.image_url ||
                                "assets/img/users/user.png"
                              }
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}>
                              {option.label}
                            </span>
                          </div>
                        )}
                      />
                    </ConfigProvider>
                  </div>
                )}

                {!disableReportTypeChange && (
                  <div className="ms-3">
                    <Tooltip
                      title="Warning: Please verify your report type. You can change it to another upcoming report or 'Other Report Type'."
                      color="orange"
                      open={showWarningTooltip}
                      onOpenChange={(open) => {
                        if (!open) setShowWarningTooltip(false);
                      }}
                    >
                      <select
                        id="reportType"
                        className="form-select form-select-sm"
                        value={selectedReportTypeIndex}
                        onChange={(e) => {
                          handleReportTypeChange(parseInt(e.target.value));
                          setShowWarningTooltip(false);
                        }}>
                        <option value="-1">Other Report Type</option>
                        {upcomingReportType.map((type, idx) => (
                          <option
                            key={`${type.id}-${idx}-${type.due_date}`}
                            value={idx}>
                            {`${type.name} (${humanReadableDate(
                              type?.due_date
                            )})`}
                          </option>
                        ))}
                      </select>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TiptapEditor
        content={content}
        onChange={handleContentChange}
        placeholder="Start writing your report..."
        className="w-100"
      />

      <div className="card report-footer shadow-lg">
        <div className="report-footer__content">
          <div className="report-footer__upload">
            <button
              type="button"
              className="report-footer__upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}>
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="ti ti-paperclip"></i>
                  <span>Attach Files</span>
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) =>
                e.target.files && handleFileSelect(e.target.files)
              }
              className="d-none"
            />

            <small className="report-footer__help">
              <span className="text-danger">Max {maxFileSize}MB each</span>
            </small>
          </div>

          {errors.length > 0 && (
            <div className="report-footer__errors">
              {errors.map((error, index) => (
                <span key={index} className="report-footer__error">
                  {error}
                </span>
              ))}
            </div>
          )}

          {attachments.length > 0 && (
            <div className="report-footer__attachments">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="attachment-tag">
                  <div className="attachment-tag__content">
                    <i className="ti ti-file attachment-tag__icon"></i>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="attachment-tag__name text-decoration-none">
                      {attachment.name}
                    </a>
                    <span className="attachment-tag__size">
                      ({formatFileSize(attachment.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    className="attachment-tag__remove"
                    onClick={() => removeAttachment(attachment.id)}
                    title={`Remove ${attachment.name}`}>
                    <i className="ti ti-x"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="report-footer__actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSubmit("draft")}
              disabled={isSubmitting || isUploading}>
              <i className="ti ti-device-floppy me-1"></i>
              Save Draft
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleSubmit("submitted")}
              disabled={isSubmitting || isUploading}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ti ti-send me-1"></i>
                  Submit Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateReportComponent;
